import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { pdfColors, pdfFontSize, pdfLetter, pdfSpacing } from '../theme';

interface Props {
  title: string;
  caption?: string;
  /** Optional ordinal ("01", "02" …) printed in brand ink before the title.
   *  Use on forms so a parent can say "section 3" over the phone. */
  index?: number;
  /** Keep the whole section on one page (no mid-section page break). Default
   *  false — long sections (tables of 200 rows) need to flow across pages. */
  keepTogether?: boolean;
  /** Tight variant — smaller bottom margin, for stacking info blocks. */
  dense?: boolean;
  children: React.ReactNode;
}

// Minimum body height (pt) that must fit under a heading on the same page;
// otherwise the heading moves to the next page with its content.
const HEADING_PRESENCE_AHEAD = 72;

const styles = StyleSheet.create({
  wrap: {
    height: pdfSpacing.lg + 2,
  },
  dense: {
    height: pdfSpacing.md,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginBottom: pdfSpacing.md,
    paddingBottom: 5,
    borderBottomWidth: 0.6,
    borderBottomColor: pdfColors.divider,
    borderBottomStyle: 'solid',
  },
  index: {
    fontSize: pdfFontSize.xs,
    fontWeight: 700,
    color: pdfColors.brand,
    marginRight: 8,
    letterSpacing: pdfLetter.openLabel,
  },
  title: {
    fontSize: pdfFontSize.xs,
    fontWeight: 700,
    color: pdfColors.textPrimary,
    textTransform: 'uppercase',
    letterSpacing: pdfLetter.openStrong,
  },
  caption: {
    marginLeft: 'auto',
    fontSize: pdfFontSize.xxs,
    color: pdfColors.textMuted,
  },
});

const Section: React.FC<Props> = ({ title, caption, index, keepTogether, dense, children }) => (
  <>
    <View style={styles.titleRow} wrap={false} minPresenceAhead={HEADING_PRESENCE_AHEAD}>
      {index != null && <Text style={styles.index}>{String(index).padStart(2, '0')}</Text>}
      <Text style={styles.title}>{title}</Text>
      {caption && <Text style={styles.caption}>{caption}</Text>}
    </View>
    <View wrap={!keepTogether}>{children}</View>
    <View style={dense ? styles.dense : styles.wrap} />
  </>
);

export default Section;
